import { API_HOST } from "@/const";
import { getLoginToken } from "@/models/userInfo";
import {
  AGENT_TERMINAL_STATUSES,
  ARTIFACT_TERMINAL_STATUSES,
  AgentCapabilitiesResponse,
  AgentRequestStatusResponse,
} from "@/types/agent";
import { httpPlus } from "./request";

const REQUEST_STATUS_PATH = /^\/api\/chat\/agent\/requests\/[A-Za-z0-9_-]{16,128}$/;

export interface AgentStatusResult {
  status: AgentRequestStatusResponse | null;
  etag?: string;
  notModified: boolean;
}

const gatewayUrl = (path: string) =>
  `${API_HOST.replace(/\/$/, "")}/chat-server${path}`;

let capabilityPromise: Promise<AgentCapabilitiesResponse> | null = null;
let capabilityToken = "";
let capabilityFetchedAt = 0;
const CAPABILITY_CACHE_MS = 30_000;

export const getAgentCapabilities = () => {
  const loginToken = getLoginToken();
  if (!loginToken) {
    return Promise.resolve({
      enabled: false,
      artifactEnabled: false,
    } as AgentCapabilitiesResponse);
  }
  if (
    !capabilityPromise ||
    capabilityToken !== loginToken ||
    Date.now() - capabilityFetchedAt >= CAPABILITY_CACHE_MS
  ) {
    capabilityToken = loginToken;
    capabilityFetchedAt = Date.now();
    capabilityPromise = httpPlus<AgentCapabilitiesResponse>(
      "get",
      "/chat-server/api/chat/agent/capabilities",
      undefined,
      {},
      false,
      undefined,
      undefined,
      5000,
    )
      .then((body) => {
        const value = body?.data;
        return {
          enabled: value?.enabled === true,
          artifactEnabled: value?.artifactEnabled === true,
          streamVersion: value?.streamVersion,
          businessModels: Array.isArray(value?.businessModels)
            ? value.businessModels
            : [],
          artifactFormats: Array.isArray(value?.artifactFormats)
            ? value.artifactFormats
            : [],
        } as AgentCapabilitiesResponse;
      })
      .catch((error) => {
        // 能力查询失败不能静默退回旧聊天链路，否则模型会在没有真实工具的
        // 情况下生成“自行运行脚本”等误导性回答。清掉缓存以允许下次重试。
        capabilityPromise = null;
        capabilityFetchedAt = 0;
        throw error;
      });
  }
  return capabilityPromise;
};

export const getAgentRequestStatus = async (
  requestId: string,
  options: { etag?: string; requestStatusPath?: string; signal?: AbortSignal } = {},
): Promise<AgentStatusResult> => {
  const fallbackPath = `/api/chat/agent/requests/${encodeURIComponent(requestId)}`;
  const path =
    options.requestStatusPath && REQUEST_STATUS_PATH.test(options.requestStatusPath)
      ? options.requestStatusPath
      : fallbackPath;
  const headers: Record<string, string> = {
    token: getLoginToken(),
    accept: "application/json",
  };
  if (options.etag) headers["If-None-Match"] = options.etag;

  const response = await fetch(gatewayUrl(path), {
    method: "GET",
    headers,
    cache: "no-cache",
    signal: options.signal,
  });
  if (response.status === 304) {
    return { status: null, etag: options.etag, notModified: true };
  }
  const body = await response.json().catch(() => null);
  if (!response.ok || (body?.code !== undefined && body.code !== 0)) {
    const error = new Error(body?.msg || body?.message || `HTTP ${response.status}`) as Error & {
      status?: number;
      code?: string | number;
      applicationRejected?: boolean;
    };
    error.status = response.status;
    error.code = body?.code;
    error.applicationRejected =
      body?.code !== undefined && String(body.code) !== "0";
    throw error;
  }
  const status = (body?.data || body) as AgentRequestStatusResponse;
  if (!status?.requestId || status.requestId !== requestId) {
    throw new Error("Agent 状态响应与当前请求不匹配");
  }
  return {
    status,
    etag: response.headers.get("ETag") || undefined,
    notModified: false,
  };
};

export const isAgentStatusPollingComplete = (
  status: AgentRequestStatusResponse,
) =>
  AGENT_TERMINAL_STATUSES.includes(status.status) &&
  (status.artifacts || []).every((artifact) =>
    ARTIFACT_TERMINAL_STATUSES.includes(artifact.status),
  );

export const requestAgentArtifactDownloadUrl = async (artifactId: string) => {
  if (!/^[A-Za-z0-9_-]{16,128}$/.test(artifactId)) {
    throw new Error("结果文件标识无效");
  }
  const response = await fetch(
    gatewayUrl(
      `/api/chat/artifacts/${encodeURIComponent(artifactId)}/download-url`,
    ),
    {
      method: "POST",
      headers: { token: getLoginToken(), accept: "application/json" },
      cache: "no-store",
    },
  );
  const body = await response.json().catch(() => null);
  if (!response.ok || (body?.code !== undefined && body.code !== 0)) {
    throw new Error(body?.msg || body?.message || `HTTP ${response.status}`);
  }
  const value = body?.data || body;
  if (value?.artifactId !== artifactId || typeof value?.signedUrl !== "string") {
    throw new Error("结果文件下载授权响应无效");
  }
  const signedUrl = new URL(value.signedUrl);
  if (signedUrl.protocol !== "https:" && signedUrl.protocol !== "http:") {
    throw new Error("结果文件下载地址无效");
  }
  return signedUrl.toString();
};
